import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';

const ConsentPage = () => {
  const { token } = useParams();
  const [state, setState] = useState('loading'); // loading | ready | consented | declined | alreadyConsented | alreadyDeclined | expired | invalid
  const [coachName, setCoachName] = useState('');
  const [clientName, setClientName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchConsent = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/consent/${token}`);
        if (res.data.alreadyConsented) { setState('alreadyConsented'); return; }
        if (res.data.alreadyDeclined) { setState('alreadyDeclined'); return; }
        setCoachName(res.data.coachName);
        setClientName(res.data.clientName);
        setState('ready');
      } catch (err) {
        if (err.response?.status === 410) setState('expired');
        else setState('invalid');
      }
    };
    fetchConsent();
  }, [token]);

  const handleRespond = async (response) => {
    setSubmitting(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/consent/${token}/respond`, { response });
      setState(response === 'consented' ? 'consented' : 'declined');
    } catch (err) {
      if (err.response?.status === 409) {
        setState('alreadyConsented');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const styles = {
    page: {
      minHeight: '100vh',
      background: '#F0F2F5',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      fontFamily: "'Inter', -apple-system, sans-serif",
    },
    card: {
      background: '#fff',
      borderRadius: '24px',
      overflow: 'hidden',
      boxShadow: '0 20px 60px rgba(27,43,75,0.15)',
      width: '100%',
      maxWidth: '420px',
    },
    hero: {
      background: '#1B2B4B',
      padding: '40px 32px 36px',
      textAlign: 'center',
    },
    logo: {
      fontFamily: 'Georgia, serif',
      fontSize: '26px',
      color: '#fff',
      marginBottom: '4px',
    },
    logoAccent: { color: '#E8654A' },
    tagline: {
      fontSize: '12px',
      color: 'rgba(255,255,255,0.45)',
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      marginBottom: '28px',
    },
    coachCard: {
      background: 'rgba(255,255,255,0.08)',
      borderRadius: '16px',
      padding: '16px 20px',
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
      textAlign: 'left',
    },
    avatar: {
      width: '48px',
      height: '48px',
      borderRadius: '50%',
      background: '#E8654A',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '18px',
      fontWeight: 600,
      color: '#fff',
      flexShrink: 0,
    },
    coachName: { fontWeight: 600, fontSize: '15px', color: '#fff' },
    coachRole: { fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginTop: '2px' },
    body: { padding: '32px' },
    heading: {
      fontFamily: 'Georgia, serif',
      fontSize: '22px',
      color: '#1B2B4B',
      marginBottom: '12px',
      lineHeight: 1.3,
    },
    text: {
      fontSize: '14px',
      color: '#6C757D',
      lineHeight: 1.7,
      marginBottom: '24px',
    },
    whatBox: {
      background: '#F8F9FA',
      borderRadius: '14px',
      padding: '16px 18px',
      marginBottom: '24px',
    },
    whatLabel: {
      fontSize: '11px',
      fontWeight: 600,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      color: '#ADB5BD',
      marginBottom: '10px',
    },
    whatItem: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '10px',
      marginBottom: '8px',
      fontSize: '13px',
      color: '#343A40',
      lineHeight: 1.5,
    },
    dot: {
      width: '6px',
      height: '6px',
      borderRadius: '50%',
      background: '#E8654A',
      flexShrink: 0,
      marginTop: '6px',
    },
    primaryBtn: {
      background: '#E8654A',
      color: '#fff',
      border: 'none',
      borderRadius: '14px',
      padding: '16px',
      fontSize: '15px',
      fontWeight: 600,
      width: '100%',
      cursor: 'pointer',
      marginBottom: '10px',
      fontFamily: 'inherit',
      opacity: 1,
    },
    secondaryBtn: {
      background: 'transparent',
      color: '#6C757D',
      border: 'none',
      padding: '12px',
      fontSize: '13px',
      width: '100%',
      cursor: 'pointer',
      fontFamily: 'inherit',
    },
    footer: {
      padding: '16px 32px 28px',
      borderTop: '1px solid #F1F3F5',
      textAlign: 'center',
      fontSize: '11px',
      color: '#ADB5BD',
      lineHeight: 1.6,
    },
    iconLg: {
      fontSize: '3rem',
      textAlign: 'center',
      marginBottom: '16px',
    },
    confirmedHeading: {
      fontFamily: 'Georgia, serif',
      fontSize: '22px',
      color: '#1B2B4B',
      textAlign: 'center',
      marginBottom: '10px',
    },
    confirmedText: {
      fontSize: '14px',
      color: '#6C757D',
      lineHeight: 1.7,
      textAlign: 'center',
    },
  };

  const initials = coachName ? coachName.split(' ').map(n => n[0]).join('').slice(0, 2) : '?';

  const renderContent = () => {
    switch (state) {
      case 'loading':
        return (
          <div style={{ ...styles.body, textAlign: 'center' }}>
            <p style={{ color: '#6C757D' }}>Loading...</p>
          </div>
        );

      case 'ready':
        return (
          <>
            <div style={styles.body}>
              <div style={styles.heading}>A quick note before your sessions</div>
              <p style={styles.text}>
                {coachName} uses AirFlyp to transcribe coaching sessions. This helps them give you better support — no notes to juggle, full attention on you.
              </p>
              <div style={styles.whatBox}>
                <div style={styles.whatLabel}>What this means</div>
                {[
                  'Sessions are transcribed by AI, not a human',
                  'Only your coach can access the transcript',
                  'You can withdraw consent any time',
                  'Sessions are encrypted and never sold',
                ].map((item, i) => (
                  <div key={i} style={styles.whatItem}>
                    <div style={styles.dot} />
                    {item}
                  </div>
                ))}
              </div>
              <button
                style={{ ...styles.primaryBtn, opacity: submitting ? 0.7 : 1 }}
                onClick={() => handleRespond('consented')}
                disabled={submitting}
              >
                ✓ I'm OK with this
              </button>
              <button
                style={styles.secondaryBtn}
                onClick={() => handleRespond('declined')}
                disabled={submitting}
              >
                No thanks — don't transcribe my sessions
              </button>
            </div>
            <div style={styles.footer}>
              Your response is logged with a timestamp for your coach's records.<br />
              Questions? Contact your coach directly.
            </div>
          </>
        );

      case 'consented':
        return (
          <div style={{ ...styles.body, textAlign: 'center', padding: '40px 32px' }}>
            <div style={{ fontSize: '3rem', marginBottom: '16px' }}>✅</div>
            <div style={styles.confirmedHeading}>You're all set</div>
            <p style={styles.confirmedText}>
              Thanks for confirming. {coachName} will be notified, and your sessions can now be transcribed.<br /><br />
              You can change your mind at any time — just let your coach know.
            </p>
          </div>
        );

      case 'declined':
        return (
          <div style={{ ...styles.body, textAlign: 'center', padding: '40px 32px' }}>
            <div style={{ fontSize: '3rem', marginBottom: '16px' }}>👋</div>
            <div style={styles.confirmedHeading}>Got it</div>
            <p style={styles.confirmedText}>
              No problem — your sessions won't be transcribed. {coachName} has been notified.
            </p>
          </div>
        );

      case 'alreadyConsented':
        return (
          <div style={{ ...styles.body, textAlign: 'center', padding: '40px 32px' }}>
            <div style={{ fontSize: '3rem', marginBottom: '16px' }}>✅</div>
            <div style={styles.confirmedHeading}>Already confirmed</div>
            <p style={styles.confirmedText}>You've already given consent for your sessions to be transcribed.</p>
          </div>
        );

      case 'alreadyDeclined':
        return (
          <div style={{ ...styles.body, textAlign: 'center', padding: '40px 32px' }}>
            <div style={{ fontSize: '3rem', marginBottom: '16px' }}>👋</div>
            <div style={styles.confirmedHeading}>Already responded</div>
            <p style={styles.confirmedText}>You've already declined transcription for your sessions.</p>
          </div>
        );

      case 'expired':
        return (
          <div style={{ ...styles.body, textAlign: 'center', padding: '40px 32px' }}>
            <div style={{ fontSize: '3rem', marginBottom: '16px' }}>⏰</div>
            <div style={styles.confirmedHeading}>Link expired</div>
            <p style={styles.confirmedText}>This consent link has expired. Ask your coach to send a new one.</p>
          </div>
        );

      default:
        return (
          <div style={{ ...styles.body, textAlign: 'center', padding: '40px 32px' }}>
            <div style={{ fontSize: '3rem', marginBottom: '16px' }}>❌</div>
            <div style={styles.confirmedHeading}>Invalid link</div>
            <p style={styles.confirmedText}>This link doesn't look right. Check with your coach for a valid consent link.</p>
          </div>
        );
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.hero}>
          <div style={styles.logo}>Air<span style={styles.logoAccent}>Flyp</span></div>
          <div style={styles.tagline}>AI-powered coaching sessions</div>
          {state === 'ready' && (
            <div style={styles.coachCard}>
              <div style={styles.avatar}>{initials}</div>
              <div>
                <div style={styles.coachName}>{coachName}</div>
                <div style={styles.coachRole}>Coaching Session Consent</div>
              </div>
            </div>
          )}
        </div>
        {renderContent()}
      </div>
    </div>
  );
};

export default ConsentPage;
